import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { KeyRound, User, Clock, Globe, Shield } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PasswordChangeLogs = ({ user, onLogout }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/admin/password-change-logs`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 100 }
      });
      setLogs(response.data.logs || []);
    } catch (error) {
      toast.error('Failed to fetch password change logs');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString();
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <Sidebar user={user} onLogout={onLogout} />
      <main className="flex-1 p-8 overflow-auto" data-testid="password-change-logs">
        <div className="max-w-6xl mx-auto space-y-8">
          {/* Header */}
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/20">
              <KeyRound className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Password Change Logs</h1>
              <p className="text-slate-400">Track all password changes across the system</p>
            </div>
          </div>

          {/* Logs Table */}
          <Card className="bg-gradient-to-br from-slate-800/80 to-slate-800/40 border-slate-700/50 shadow-lg backdrop-blur-sm">
            <CardHeader className="border-b border-slate-700/50">
              <CardTitle className="text-base font-semibold text-white flex items-center gap-2">
                <Shield className="w-5 h-5 text-amber-500" />
                Recent Password Changes
                <Badge variant="outline" className="ml-2 text-slate-400 border-slate-600">
                  {logs.length} records
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b border-slate-700/50 bg-slate-800/50">
                      <TableHead className="text-slate-400 font-medium">User</TableHead>
                      <TableHead className="text-slate-400 font-medium">Email</TableHead>
                      <TableHead className="text-slate-400 font-medium">Changed At</TableHead>
                      <TableHead className="text-slate-400 font-medium">IP Address</TableHead>
                      <TableHead className="text-slate-400 font-medium">Changed By</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8">
                          <div className="flex items-center justify-center gap-2 text-slate-400">
                            <div className="w-5 h-5 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                            Loading logs...
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : logs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-slate-400">
                          No password changes recorded yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      logs.map((log, index) => (
                        <TableRow key={log.id || index} className="border-b border-slate-700/50 hover:bg-slate-700/30 transition-colors">
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center">
                                <User className="w-4 h-4 text-slate-400" />
                              </div>
                              <span className="text-white font-medium">{log.user_name}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-slate-300">{log.user_email}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2 text-slate-300">
                              <Clock className="w-4 h-4 text-slate-500" />
                              {formatDate(log.changed_at)}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2 text-slate-300">
                              <Globe className="w-4 h-4 text-slate-500" />
                              {log.ip_address}
                            </div>
                          </TableCell>
                          <TableCell>
                            {log.changed_by === 'self' ? (
                              <Badge className="bg-emerald-500/20 text-emerald-400 border-0">
                                Self
                              </Badge>
                            ) : (
                              <Badge className="bg-amber-500/20 text-amber-400 border-0">
                                Admin: {log.changed_by}
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </main>
    </div>
  );
};

export default PasswordChangeLogs;
